// [내 계정] 창(2026-10-23 사용자 요청 — "비밀번호 변경 메뉴는 있어야 한다").
// 사이드바 아래 이름을 누르면 열린다. 두 가지를 한다:
//   - 내 이메일(= 로그인 ID) 변경 — 종전에는 [기기] 화면의 등록 요청 칸에 있던 것을 이리로 옮겼다(그 자리엔 읽기 전용으로 남김).
//   - 비밀번호 변경 — 지금 비밀번호 + 새 비밀번호(두 번). 성공하면 **다른 기기·브라우저 로그인이 끊기고 이 창은 유지**된다
//     (서버 `POST /api/auth/password` 가 새 토큰 쌍을 주고 `Api.changePassword` 가 바꿔 낀다 — 사용자 결정).
import { useCallback, useState } from 'react';
import type { Api, MyProfile } from '../../api';
import { EMAIL_CHECK_REQUIRED_MESSAGE } from '../../emailRules';
import { EmailCheckField } from '../EmailCheckField';
import { PasswordField } from '../PasswordField';
import { useEmailCheck } from '../useEmailCheck';

/** 새 비밀번호 최소 길이 — 🔴 서버 `ChangePasswordDto`(8자)와 같아야 한다. 화면은 미리 알려 주는 용도(최종 판정은 서버). */
export const PASSWORD_MIN_LENGTH = 8;

interface Props {
  api: Api;
  me: MyProfile;
  /** 이메일을 바꿨을 때 — 바깥(사이드바·기기 화면)의 내 프로필을 갱신한다. */
  onProfileChanged: (p: MyProfile) => void;
  onClose: () => void;
}

/** 비밀번호 변경 입력값을 검사한다(서버에 보내기 전 안내용). 문제없으면 빈 문자열. */
export function passwordFormProblem(current: string, next: string, again: string): string {
  if (!current || !next || !again) return '세 칸을 모두 입력하세요.';
  if (next.length < PASSWORD_MIN_LENGTH) return `새 비밀번호는 ${PASSWORD_MIN_LENGTH}자 이상이어야 합니다.`;
  if (next !== again) return '새 비밀번호 두 칸이 서로 다릅니다.';
  if (next === current) return '새 비밀번호가 지금 비밀번호와 같습니다.';
  return '';
}

export function MyAccountDialog({ api, me, onProfileChanged, onClose }: Props) {
  // ── 비밀번호 ──
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwDone, setPwDone] = useState('');

  // ── 이메일 ──
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailInput, setEmailInput] = useState(me.email);
  const [emailPassword, setEmailPassword] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailDone, setEmailDone] = useState('');
  const emailCheck = useEmailCheck(api, emailInput);

  const savePassword = useCallback(async () => {
    const problem = passwordFormProblem(current, next, again);
    if (problem) {
      setPwError(problem);
      return;
    }
    setPwBusy(true);
    setPwError('');
    setPwDone('');
    try {
      await api.changePassword(current, next);
      setCurrent('');
      setNext('');
      setAgain('');
      setPwDone('비밀번호를 바꿨습니다. 다른 기기·브라우저의 로그인은 끊겼습니다(이 창과 진행 중인 원격 세션은 그대로).');
    } catch (e) {
      setPwError((e as Error).message);
    } finally {
      setPwBusy(false);
    }
  }, [api, current, next, again]);

  const saveEmail = useCallback(async () => {
    const value = emailInput.trim();
    if (!value || !emailPassword) {
      setEmailError('새 이메일과 지금 비밀번호를 입력하세요.');
      return;
    }
    if (!emailCheck.view.confirmed) {
      emailCheck.touch();
      setEmailError(emailCheck.view.formatOk ? EMAIL_CHECK_REQUIRED_MESSAGE : emailCheck.view.message || '이메일 형식이 아닙니다.');
      return;
    }
    setEmailBusy(true);
    setEmailError('');
    setEmailDone('');
    try {
      const updated = await api.updateMyEmail(value, emailPassword);
      onProfileChanged(updated);
      setEmailEditing(false);
      setEmailPassword('');
      setEmailDone(`이메일을 ${updated.email} 로 바꿨습니다. 다음 로그인부터 이 주소로 로그인하세요.`);
    } catch (e) {
      setEmailError((e as Error).message);
    } finally {
      setEmailBusy(false);
    }
  }, [api, emailCheck, emailInput, emailPassword, onProfileChanged]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && !pwBusy && !emailBusy && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="modal-box account-box" role="dialog" aria-modal="true" aria-labelledby="account-title">
        <div className="modal-head">
          <h2 id="account-title">내 계정</h2>
          <button className="modal-x" aria-label="닫기" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="hint">{me.userName}</div>

        <h3 className="account-section">이메일(로그인 ID)</h3>
        {emailEditing ? (
          <>
            <EmailCheckField
              value={emailInput}
              onChange={setEmailInput}
              emailCheck={emailCheck}
              placeholder="새 이메일(로그인 ID)"
              ariaLabel="새 이메일"
              autoFocus
            />
            <PasswordField
              value={emailPassword}
              onChange={setEmailPassword}
              placeholder="지금 비밀번호"
              aria-label="이메일 변경용 지금 비밀번호"
              style={{ width: '100%' }}
              onKeyDown={(e) => e.key === 'Enter' && !emailBusy && void saveEmail()}
            />
            <div className="row" style={{ gap: 8 }}>
              <button onClick={() => void saveEmail()} disabled={emailBusy || !emailCheck.view.confirmed}>
                {emailBusy ? '저장 중…' : '이메일 저장'}
              </button>
              <button className="ghost" onClick={() => setEmailEditing(false)} disabled={emailBusy}>
                취소
              </button>
            </div>
          </>
        ) : (
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <b>{me.email}</b>
            <button
              className="linklike"
              onClick={() => {
                setEmailInput(me.email);
                setEmailPassword('');
                setEmailError('');
                setEmailDone('');
                emailCheck.reset();
                setEmailEditing(true);
              }}
            >
              변경
            </button>
          </div>
        )}
        {emailError && <div className="hint" style={{ color: 'var(--err)' }}>{emailError}</div>}
        {emailDone && <div className="hint" style={{ color: 'var(--ok)' }}>{emailDone}</div>}

        <h3 className="account-section">비밀번호 변경</h3>
        <PasswordField
          value={current}
          onChange={setCurrent}
          placeholder="지금 비밀번호"
          aria-label="지금 비밀번호"
          style={{ width: '100%' }}
        />
        <PasswordField
          value={next}
          onChange={setNext}
          placeholder={`새 비밀번호(${PASSWORD_MIN_LENGTH}자 이상)`}
          aria-label="새 비밀번호"
          style={{ width: '100%' }}
        />
        <PasswordField
          value={again}
          onChange={setAgain}
          placeholder="새 비밀번호 확인"
          aria-label="새 비밀번호 확인"
          style={{ width: '100%' }}
          onKeyDown={(e) => e.key === 'Enter' && !pwBusy && void savePassword()}
        />
        <div className="hint">바꾸면 다른 기기·브라우저의 로그인과 자동 로그인이 끊깁니다. 이 창과 진행 중인 원격 세션은 유지됩니다.</div>
        {pwError && <div className="hint" style={{ color: 'var(--err)' }}>{pwError}</div>}
        {pwDone && <div className="hint" style={{ color: 'var(--ok)' }}>{pwDone}</div>}
        <button onClick={() => void savePassword()} disabled={pwBusy}>
          {pwBusy ? '바꾸는 중…' : '비밀번호 변경'}
        </button>
      </div>
    </div>
  );
}
